import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  // Les routes super-admin portent leur propre token (gss_sa_token), pose par
  // saApi() en surcharge de header. Ne JAMAIS l'ecraser avec le token
  // utilisateur normal (gss_token) : sinon le backend recoit le mauvais token
  // sur /super-admin/* -> 401 -> purge + redirect (flash au login super-admin).
  if (config.url?.includes('/super-admin/')) {
    return config;
  }
  const token = localStorage.getItem('gss_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => {
    // ⚠️ LA REACTIVATION EST IMMEDIATE ET N'EXIGE AUCUNE INTERVENTION.
    //    Des qu'une ECRITURE repasse, la suspension est levee cote client : le
    //    bandeau disparait sans rechargement, sans reconnexion, sans purge de
    //    cache. Un etat qui ne se leve que « au prochain login » serait une
    //    reactivation a moitie.
    const m = (res.config?.method || '').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(m) && localStorage.getItem('gss_tenant_suspendu')) {
      localStorage.removeItem('gss_tenant_suspendu');
      window.dispatchEvent(new CustomEvent('gss:tenant-reactive'));
    }
    return res;
  },
  (err) => {
    const url = err.config?.url || '';
    const isAuthRoute = url === '/auth/login' || url === '/auth/force-change-password' || url === '/super-admin/login';
    if (err.response?.status === 401 && !isAuthRoute) {
      if (url.startsWith('/super-admin/')) {
        localStorage.removeItem('gss_sa_token');
        localStorage.removeItem('gss_sa_user');
        window.location.href = '/super-admin/login';
      } else {
        localStorage.removeItem('gss_token');
        localStorage.removeItem('gss_user');
        window.location.href = '/login';
      }
    }
    /* ⚠️ TENANT SUSPENDU — LE REFUS SE VOIT, IL NE SE DEVINE PAS.
     *    Le serveur repond 403 + `code: 'TENANT_SUSPENDU'`. On memorise l'etat
     *    et on previent l'application : le bandeau permanent s'affiche, et
     *    l'utilisateur comprend POURQUOI son action n'est pas passee.
     *    ⚠️ POSE ICI, PAS DANS LES ECRANS : il y a des dizaines de formulaires ;
     *    un bandeau branche ecran par ecran serait oublie quelque part, et cet
     *    ecran-la afficherait un echec muet. L'intercepteur ne s'oublie pas.
     *    ⚠️ On ne deconnecte PAS : la lecture reste ouverte, c'est tout l'objet
     *    de la suspension en lecture seule. */
    if (err.response?.status === 403 && err.response?.data?.code === 'TENANT_SUSPENDU') {
      const message = err.response.data.error || 'Acces suspendu — contactez votre fournisseur.';
      try {
        localStorage.setItem('gss_tenant_suspendu', message);
        window.dispatchEvent(new CustomEvent('gss:tenant-suspendu', { detail: { message } }));
      } catch { /* stockage indisponible : le bandeau ne doit pas casser l'appel */ }
    }

    return Promise.reject(err);
  }
);

/** Le tenant est-il suspendu, d'apres le dernier refus recu ? */
export const tenantSuspendu = () => localStorage.getItem('gss_tenant_suspendu');
/** A appeler quand une ECRITURE repasse : la suspension est levee. */
export const leverSuspension = () => localStorage.removeItem('gss_tenant_suspendu');

// L'URL d'un document se CALCULE depuis l'id de sa ligne — elle n'est plus
// stockee en base. La colonne (`document_url`, `photo_url`...) ne sert plus
// qu'a repondre « ce document existe-t-il ? ».
export const urlDocument = (categorie, id, variante) =>
  `/api/documents/${categorie}/${id}` + (variante !== undefined && variante !== null ? `/${variante}` : '');

export const fetchFile = async (url) => {
  const token = localStorage.getItem('gss_token');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  return res.blob();
};

export default api;
